import { Environments, Property, PropertyCMSPath } from '@powdr/constants';

import { getKeyByValue } from './app-utils';

const getPropertyHost = (property, isDevelopment = process.env.GATSBY_ACTIVE_ENV === Environments.DEVELOPMENT) => (
  `${isDevelopment ? `dev.${property}` : property}${process.env.GATSBY_TLD_OVERRIDE || '.com'}`
);

export const getOriginDomain = () => `https://www.${getPropertyHost(process.env.GATSBY_PROPERTY_NAME)}`;

export const getOriginDomainForProperty = (property, isDevelopment = null) => (
  property ? `https://www.${getPropertyHost(property, isDevelopment ?? process.env.GATSBY_ACTIVE_ENV === Environments.DEVELOPMENT)}` : null
);

export const getDrupalDomain = (property, isDevelopment = null) => (
  `https://cms.${getPropertyHost(property, isDevelopment ?? process.env.GATSBY_ACTIVE_ENV === Environments.DEVELOPMENT)}`
);

export const getDrupalInlinePath = (property) => getKeyByValue(PropertyCMSPath, property);

export const isDrupalDevUrl = (url) => {
  try {
    return /^cms\.dev\./.test(new URL(url).hostname);
  } catch {
    return false;
  }
};

export const getPropertyFromDrupalUrl = (url) => {
  try {
    const { hostname } = new URL(url);
    const cmsMatch = hostname.match(/^cms(?:\.dev)?\.([^.]+)/);
    if (!cmsMatch) return null;

    const hostProperty = cmsMatch[1];
    if (Object.values(Property).includes(hostProperty)) return hostProperty;

    return getKeyByValue(PropertyCMSPath, hostProperty);
  } catch {
    return null;
  }
};

export const getDrupalViewApiDomain = () => `${getDrupalDomain(process.env.GATSBY_PROPERTY_NAME)}/api/v2?display=rest_export_1`;

// Temporary: set to true to route Drupal view `externalEndpoint` values through Node.
export const ENABLE_EXTERNAL_DRUPAL_VIEW_PROXY = false;

const NODE_API_PATH_PREFIXES = [
  '/content',
  '/dor',
  '/cams',
  '/email',
  '/parking',
  '/recaptcha',
  '/api/v1',
];

export const isNodeApiEndpoint = (endpoint) => {
  if (!endpoint || /^https?:\/\//i.test(endpoint)) return false;

  const path = (endpoint.startsWith('/') ? endpoint : `/${endpoint}`).split('?')[0];
  return NODE_API_PATH_PREFIXES.some((prefix) => path === prefix || path.startsWith(`${prefix}/`));
};

export const isDrupalViewEndpoint = (endpoint) => {
  if (!endpoint) return false;
  if (isNodeApiEndpoint(endpoint)) return false;
  if (/^https?:\/\//i.test(endpoint)) return endpoint.includes('/api/v2');
  if (endpoint.includes('/api/v2')) return true;
  if (endpoint.includes('view=')) return true;
  if (!endpoint.startsWith('/') && !endpoint.includes('/')) return true;
  return false;
};

export const shouldProxyExternalDrupalView = (endpoint) => (
  ENABLE_EXTERNAL_DRUPAL_VIEW_PROXY
  && !isNodeApiEndpoint(endpoint)
  && isDrupalViewEndpoint(endpoint)
);

export const resolveExternalEndpointUrl = (endpoint) => {
  if (!endpoint) return null;

  if (/^https?:\/\//i.test(endpoint)) return endpoint;

  if (endpoint.startsWith('/api/v2')) {
    return `${getDrupalDomain(process.env.GATSBY_PROPERTY_NAME)}${endpoint}`;
  }

  if (endpoint.startsWith('?')) {
    return `${getDrupalDomain(process.env.GATSBY_PROPERTY_NAME)}/api/v2${endpoint}`;
  }

  if (endpoint.startsWith('&')) {
    return `${getDrupalViewApiDomain()}${endpoint}`;
  }

  if (isDrupalViewEndpoint(endpoint)) {
    if (endpoint.includes('view=')) {
      return `${getDrupalDomain(process.env.GATSBY_PROPERTY_NAME)}/api/v2?${endpoint.replace(/^[?&]/, '')}`;
    }
    return `${getDrupalViewApiDomain()}&view=${endpoint}`;
  }

  const path = endpoint.startsWith('/') ? endpoint : `/${endpoint}`;
  return `${getNodeApiDomain()}${path}`;
};

const DRUPAL_VIEW_PROXY_OMITTED_PARAMS = ['display', 'view', 'property'];

const getDrupalViewSearchParams = (endpoint) => {
  if (/^https?:\/\//i.test(endpoint)) {
    return new URL(endpoint).searchParams;
  }

  if (endpoint.startsWith('/api/v2')) {
    return new URL(`https://placeholder.local${endpoint}`).searchParams;
  }

  if (endpoint.startsWith('?') || endpoint.startsWith('&') || endpoint.includes('view=')) {
    return new URLSearchParams(endpoint.replace(/^[?&]/, ''));
  }

  return new URLSearchParams({ view: endpoint });
};

/**
 * Rewrite a Drupal view `externalEndpoint` through the current site's Node API.
 * Regular View / getViewAll fetches should keep using getDrupalViewApiDomain().
 */
export const resolveExternalEndpointProxyUrl = (endpoint) => {
  if (!endpoint) return null;

  const searchParams = getDrupalViewSearchParams(endpoint);
  const view = searchParams.get('view')
    || (!endpoint.includes('=') && !endpoint.includes('/') ? endpoint : null);
  const property = getPropertyFromDrupalUrl(endpoint) || process.env.GATSBY_PROPERTY_NAME;
  const proxyParams = new URLSearchParams();

  if (view) proxyParams.set('view', view);
  if (property) proxyParams.set('property', property);

  searchParams.forEach((value, key) => {
    if (!DRUPAL_VIEW_PROXY_OMITTED_PARAMS.includes(key) && value) {
      proxyParams.append(key, value);
    }
  });

  return `${getNodeApiDomain()}/content/view?${proxyParams.toString()}`;
};

export const getExternalEndpointContext = (endpoint) => {
  if (!endpoint) return null;

  const url = /^https?:\/\//i.test(endpoint) ? endpoint : resolveExternalEndpointUrl(endpoint);
  const property = getPropertyFromDrupalUrl(url);

  if (property) {
    return {
      property,
      isDevelopment: isDrupalDevUrl(url),
    };
  }

  if (isDrupalViewEndpoint(endpoint)) {
    return {
      property: process.env.GATSBY_PROPERTY_NAME,
      isDevelopment: process.env.GATSBY_ACTIVE_ENV === Environments.DEVELOPMENT,
    };
  }

  return null;
};

export const getExternalEndpointProperty = (endpoint) => (
  getExternalEndpointContext(endpoint)?.property ?? null
);

export const getNodeApiDomain = () => {
  const apiPath = 'api/v1';
  const sitename = (process.env.GATSBY_PROPERTY_NAME === Property.RENDEZVAN)
    ? Property.MTBACHELOR
    : process.env.GATSBY_PROPERTY_NAME;

  if (process.env.GATSBY_NODE_API === Environments.LOCAL) {
    // When running powdr-node locally you need to spoof your local ie. {property}.dev.local:8000
    return `http://api.${sitename}.dev.local:8080/${apiPath}`;
  }

  return `https://api.${(process.env.GATSBY_NODE_API === Environments.DEVELOPMENT)
    ? `dev.${sitename}`
    : sitename}${process.env.GATSBY_TLD_OVERRIDE || '.com'}/${apiPath}`;
};
